import React, { useContext, useMemo, useEffect, useState, useCallback, useRef } from "react";
import "./styles.css";
import { ReactTable, DatePicker } from "../../Components/Molecules";
import { AutoCompleteMultiSelect, DatePickerInput, StatsCard, Title } from "../../Components/Atoms";
import { StoreContext } from "../../Library/contexts";
import { LightBulbIcon, LocationIcon } from "../../Assets/images/svgs";
import { VEHICLE_STATISTICS_HEADERS } from "../../Library/tableHeaders";
import { useNavigate } from "react-router-dom";
import { getISODate, secondsToHms } from "../../Library/datetime";
import { DATASET_COLORS } from "../../Assets/styles/colors";
import PATHS from "../../Routes/paths";
import { getAllRideStats, getStatesByCountryId } from "../../APIs";
import { DATE_OPTIONS, DATE_RANGES } from "../../Library/constants";
import { getStatLabel } from "../../Library/helpers";
import Loader from "../Loader";

const STATS_CARDS = [
  { title: "Distance Travelled", key: "dst", format: "distance" },
  { title: "Duration", key: "dur", format: "duration" },
  { title: "Top Speed", key: "tps", format: "speed" },
];

const AllVehicleStats = () => {
  const { filters, userVehicles, allVehicleStats, setAllVehicleStats, allStates, setAllStates } = useContext(StoreContext);
  const navigate = useNavigate();

  const [vids, setVids] = useState([]);
  const [vehiclesMap, setVehiclesMap] = useState(new Map());
  const [rideStats, setRideStats] = useState([]);

  const [overAllStats, setOverAllStats] = useState({});
  const [vehicleWithMaxDistance, setVehicleWithMaxDistance] = useState({});
  const [tableData, setTableData] = useState([]);
  const [showLoading, setShowLoading] = useState(true);

  const [fetchedStates, setFetchedStates] = useState([]);
  const originalTableData = useRef([]);

  const vehiclesCountCalculated = useRef(false);

  const [userVehiclesData, setUserVehiclesData] = useState([]);

  const columns = useMemo(
    () =>
      Object.values(VEHICLE_STATISTICS_HEADERS).map((header) => {
        return {
          Header: header.nm,
          accessor: header.key,
          disableSortBy: !header.sorting,
          Cell: (row) => {
            switch (header.key) {
              case "reg": {
                return (
                  <span
                    className="avs-link"
                    onClick={() =>
                      navigate(
                        `${PATHS.ALL_VEHICLE_STATS}/${
                          PATHS.SINGLE_VEHICLE_STATS
                        }?idx=${row.row.original.idx}&isSvs=${true}`,
                        { state: { vehicle: vehiclesMap.get(row?.row.original.idx) } }
                      )
                    }
                  >
                    {row.value}
                  </span>
                );
              }
              case "dst": {
                return (
                  <span className="avs-cell-value">
                    {getStatLabel(row.value, "distance", 2, 1)}
                  </span>
                );
              }
              case "dur": {
                return (
                  <span className="avs-cell-value">
                    {getStatLabel(row.value, "duration", 2, 1)}
                  </span>
                );
              }
              case "avs": {
                return (
                  <span className="avs-cell-value">
                    {getStatLabel(row.value, "speed", 2, 1)}
                  </span>
                );
              }
              case "tps": {
                return (
                  <span className="avs-cell-value">
                    {getStatLabel(row.value, "speed", 2, 1)}
                  </span>
                );
              }
              default: {
                return (
                  <span className="avs-cell-value">{row.value || "-"}</span>
                );
              }
            }
          },
        };
      }),
    [navigate, vehiclesMap, filters.range]
  );

  const getProcessedData = (vehicleData, type) => {
    const processedData = vehicleData.map((data) => {
      return {
        id: data.idx,
        label: data.reg,
        count:
          type === "dst"
            ? data[type] / 1000
            : type === "dur"
            ? data[type] / (60*60)
            : data[type],
        color:
          type === "dst"
            ? DATASET_COLORS.ORANGE
            : type === "dur"
            ? DATASET_COLORS.SUCCESS
            : DATASET_COLORS.CYAN,
      };
    });
    processedData.sort((a, b) => a.id - b.id);
    return processedData;
  };

  const distanceStats = useMemo(() => {
    if (!rideStats || !rideStats.length) return [];
    return getProcessedData(tableData, "dst");
  }, [tableData, rideStats]);

  const durationStats = useMemo(() => {
    if (!rideStats || !rideStats.length) return [];
    return getProcessedData(tableData, "dur");
  }, [tableData, rideStats]);

  const speedStats = useMemo(() => {
    if (!rideStats || !rideStats.length) return [];
    return getProcessedData(tableData, "avs");
  }, [tableData, rideStats]);

  const axisStyles = {
    x: {
      ticks: { autoSkip: false, maxRotation: 90, minRotation: 22.5 },
    },
  };

  const graphs = useMemo(() => {
    if (distanceStats && durationStats && speedStats) {
      return [
        {
          title: "Distance Travelled",
          stats: distanceStats,
          parser: {
            x: "label",
            y: "count",
            yUnit: "km",
            yLabel: "Distance Travelled",
            xLabel: "Vehicles",
          },
          axisStyles: axisStyles,
          formatter: (distance) => `${Number(distance).toFixed(2)} km`,
        },
        {
          title: "Duration",
          stats: durationStats,
          parser: {
            x: "label",
            y: "count",
            yUnit: "hrs",
            yLabel: "Time Travelled",
            xLabel: "Vehicles",
          },
          axisStyles: axisStyles,
          formatter: (duration) => secondsToHms(duration * 60 * 60),
        },
        {
          title: "Average Speed",
          stats: speedStats,
          parser: {
            x: "label",
            y: "count",
            yUnit: "km/hr",
            yLabel: "Average Speed",
            xLabel: "Vehicles",
          },
          axisStyles: axisStyles,
          formatter: (speed) => `${Number(speed).toFixed(2)} km/hr`,
        },
      ];
    }
    return [];
  }, [distanceStats, durationStats, speedStats]);

  const computeStats = (data) => {
    let distanceTravelled = 0;
    let duration = 0;
    let topSpeed = 0;

    data.forEach((stat) => {
      distanceTravelled += stat.dst;
      duration += stat.dur;
      topSpeed = Math.max(topSpeed, stat.tps);
    });

    setOverAllStats({
      dst: distanceTravelled,
      dur: duration,
      tps: topSpeed,
    });
  };

  const getRideStats = useCallback((vehicleIds) => {
    let vehStats = allVehicleStats[filters.range];
    if (filters.range !== DATE_OPTIONS.CUSTOM && allVehicleStats && vehStats) {
      setRideStats(vehStats);
      computeStats(vehStats);
      setShowLoading(false);
      return;
    }

    setShowLoading(true);
    let request = { from: DATE_RANGES[filters.range].from, to: DATE_RANGES[filters.range].to, vids: vehicleIds, bulkFetch: true };
    getAllRideStats(request, (err, data) => {
      if (err) {
        console.error("Error fetching ride stats", err);
      } else {
        if (data && data.dailyRidesStats) {
          let stats = data.dailyRidesStats.length ? data.dailyRidesStats : [];
          setAllVehicleStats({ ...allVehicleStats, [filters.range]: stats });
          setRideStats(stats);
          computeStats(stats);
        }
      }
      setShowLoading(false);
    });
  }, [ filters.range ]);

  const handleBikesData = (data) => {
    let vehicleIds = []
    vehicleIds = data.map((veh) => veh.idx);
    setVids(vehicleIds);
    getRideStats(vehicleIds);

    let vehMap = new Map();
    data.forEach((vehicle) => {
      vehMap.set(vehicle.idx, vehicle);
    });
    setVehiclesMap(vehMap);
  };

  const areUserVehiclesUpdated = (prevVehicles, newVehicles) => {
    if (!prevVehicles || !newVehicles) return false;
    if (prevVehicles.length !== newVehicles.length) return true;
    return false;
  }

  useEffect(() => {
    if (!areUserVehiclesUpdated(userVehiclesData, userVehicles)) return;
    if (userVehicles && userVehicles.length) {
      setUserVehiclesData(userVehicles);
      handleBikesData(userVehicles);
    }
  }, [ userVehicles ]);

  useEffect(() => {
    if (!rideStats || !rideStats.length) {
      setTableData([]);
      setVehicleWithMaxDistance({});
      return;
    }

    let tableData = [];
    let maxDistance = 0;
    let maxDistanceVehicle = {};

    vehiclesMap.forEach((vehicle) => {
      const vehicleStats = rideStats.filter((stat) => stat.vid === vehicle.idx);

      const totalDistance = vehicleStats.reduce((acc, curr) => acc + curr.dst, 0);
      const totalDuration = vehicleStats.reduce((acc, curr) => acc + curr.dur, 0);
      const totalAverageSpeed = vehicleStats.reduce((acc, curr) => acc + curr.avs, 0) / vehicleStats.length;
      let topSpeed = Math.max(...vehicleStats.map((stat) => stat.tps));
      if(topSpeed === -Infinity) topSpeed = 0;

      tableData.push({
        idx: vehicle.idx,
        reg: vehicle.reg,
        mo: vehicle.mo,
        dst: totalDistance,
        dur: totalDuration,
        avs: totalAverageSpeed,
        tps: topSpeed,
      });

      if (totalDistance > maxDistance) {
        maxDistance = totalDistance;
        maxDistanceVehicle = {
          reg: vehicle.reg,
          distance: totalDistance / 1000,
        };
      }
    });

    setTableData(tableData);
    setVehicleWithMaxDistance(maxDistanceVehicle);
  }, [rideStats, vehiclesMap]);


  useEffect(() => {
    if (vids && vids.length && filters.range !== DATE_OPTIONS.CUSTOM) getRideStats(vids);
  }, [filters.range, getRideStats]);

  const fetchAllStates = async () => {
    if (allStates && allStates.length) {
      setFetchedStates(allStates);
      return;
    }

    let states = await getStatesByCountryId(true);
    if (states && states.length) {
      states.forEach(state => {
        state.label = state.nm;
        state.value = state.idx;
      });

      setFetchedStates(states);
      setAllStates(states);
    }
  }

  useEffect(() => {
    fetchAllStates();
  }, []);

  useEffect(() => {
    if (!tableData || !tableData.length || !fetchedStates || !fetchedStates.length || vehiclesCountCalculated.current) return;
    let statesToShow = [];
    fetchedStates.forEach(state => {
      let stateHasVehicles = tableData.filter(data => data.reg && data.reg.toLowerCase().startsWith(state.abb?.toLowerCase()));
      if (stateHasVehicles && stateHasVehicles.length) {
        state.label = `${state.nm} (${stateHasVehicles.length})`;
        statesToShow.push(state);
      }
    });

    vehiclesCountCalculated.current = true;
    setFetchedStates(statesToShow);

  }, [ tableData, fetchedStates ]);

  const handleFilterApply = (selectedOptions, isSelectAll) => {
    if (!selectedOptions.length) return;

    if (!originalTableData.current || !originalTableData.current.length) originalTableData.current = tableData;

    let filteredTableData = [];
    if (isSelectAll) filteredTableData = originalTableData.current;
    else {
      selectedOptions.forEach(option => {
        let filteredVehicles = originalTableData.current.filter(data => {
          if (data && option && data.reg && option.abb) return data.reg.toLowerCase().startsWith(option.abb?.toLowerCase())
          return "";
        });
        filteredTableData = filteredTableData.concat(filteredVehicles);
      });
    }

    setTableData(filteredTableData);

    let vehicleIds = filteredTableData.map(data => data.idx);
    let stats = rideStats.filter(stat => vehicleIds.includes(stat.vid))
    computeStats(stats);
  }

  return showLoading ? <Loader hide={true}/> : <div className="avs-wrapper">
      <div className="avs-date-picker">
        <div className="select-state-dropdown">
          <AutoCompleteMultiSelect showListControls={true} autoFillOptions={fetchedStates} options={fetchedStates} placeholder={"All States"} icon={<LocationIcon />} handleFilterApply={handleFilterApply} />
        </div>
        <DatePicker customMaxDate={getISODate()} onClose={() => getRideStats(vids)} />
      </div>
      <div className="avs-stats">
        <div className="avs-title-container">
          <Title>Statistics ({tableData.length > 1 ? `${tableData.length} vehicles`  : "All Vehicles"})</Title>
        </div>
        <div className="avs-stats-cards">
          {STATS_CARDS.map((stat, idx) => (
            <StatsCard
              key={idx}
              format={stat.format}
              title={stat.title}
              value={overAllStats[stat.key]}
              round={stat.key === "dst"}
            />
          ))}
        </div>
        <div className="avs-stats-note">
          <LightBulbIcon />
          <span className="avs-msg">
            {vehicleWithMaxDistance.reg
            ? `${vehicleWithMaxDistance.reg} covered the most distance out of all vehicles, traveling ${(Math.round(vehicleWithMaxDistance.distance.toFixed(2) || 0).toLocaleString())} kms in the selected time period`
            : "No data available for the selected time period"}
          </span>
        </div>
      </div>
      <div className="avs-table">
        <ReactTable
          columns={columns}
          showSearch={true}
          heading={"Individual Vehicles Statistics "}
          data={tableData}
          pageSize={10}
        />
      </div>
      {/* {graphs.map((graph, idx) => (
        <div className="avs-graph-container" key={idx}>
          <div className="avs-graph-title">
            <Title>{graph.title}</Title>
          </div>
          <div className="avs-graph-wrapper">
            <div className="avs-graph widget-chart">
              <Bar
                title={graph.title}
                parser={graph.parser}
                data={graph.stats}
                tooltipFormatter={graph.formatter}
                barThickness={74}
              />
            </div>
          </div>
        </div>
      ))} */}
    </div>
};

export default AllVehicleStats;
